/**
 * SKU Table section for displaying process playbooks
 */
// Helper function to format target producing rate
export const formatTargetProducingRate = (rate: any): string => {
  if (!rate) {
    return "N/A";
  }

  if (rate.count === undefined || rate.intervalMs === undefined) {
    return JSON.stringify(rate, null, 2);
  }

  // Convert to appropriate time unit
  if (rate.intervalMs === 3600000) {
    return `${rate.count}/hour`;
  } else if (rate.intervalMs === 60000) {
    return `${rate.count}/minute`;
  } else if (rate.intervalMs === 1000) {
    return `${rate.count}/second`;
  } else {
    // Generic case - convert to the most appropriate unit
    if (rate.intervalMs >= 3600000) {
      const hours = rate.intervalMs / 3600000;
      return `${rate.count}/${hours} hour${hours !== 1 ? "s" : ""}`;
    } else if (rate.intervalMs >= 60000) {
      const minutes = rate.intervalMs / 60000;
      return `${rate.count}/${minutes} minute${minutes !== 1 ? "s" : ""}`;
    } else {
      const seconds = rate.intervalMs / 1000;
      return `${seconds === 1 ? rate.count + "/second" : rate.count + "/" + seconds + " seconds"}`;
    }
  }
};
// Helper function to format consecutive team sizes into a condensed representation
export const formatTeamSizes = (teamSizes: number[]): string => {
  if (!teamSizes || teamSizes.length === 0) {
    return "N/A";
  }

  // Sort the team sizes to make processing easier
  teamSizes.sort((a, b) => a - b);

  // Remove duplicates
  const uniqueSizes = Array.from(new Set(teamSizes));

  const ranges: string[] = [];
  let rangeStart: number | null = null;
  let rangeEnd: number | null = null;

  // Process each team size to form ranges
  uniqueSizes.forEach((size, index) => {
    // Check if this is the first item or if there's a gap in the sequence
    if (index === 0 || size > uniqueSizes[index - 1] + 1) {
      // If we were building a range, add it to the result
      if (rangeStart !== null && rangeEnd !== null) {
        if (rangeStart === rangeEnd) {
          ranges.push(`${rangeStart}`);
        } else {
          ranges.push(`${rangeStart}..${rangeEnd}`);
        }
      }
      // Start a new range
      rangeStart = size;
      rangeEnd = size;
    } else {
      // Continue the current range
      rangeEnd = size;
    }

    // If this is the last item, add the current range
    if (
      index === uniqueSizes.length - 1 &&
      rangeStart !== null &&
      rangeEnd !== null
    ) {
      if (rangeStart === rangeEnd) {
        ranges.push(`${rangeStart}`);
      } else {
        ranges.push(`${rangeStart}..${rangeEnd}`);
      }
    }
  });

  return ranges.join(", ");
};

// Utility function to convert data to CSV format
export const convertToCSV = (data: any[]) => {
  if (data.length === 0) {
    return "";
  }

  // Get headers from the first object's keys
  const headers = Object.keys(data[0]);

  // Create CSV header row
  let csvContent = headers.join(",") + "\n";

  // Add data rows
  data.forEach(item => {
    const row = headers
      .map(header => {
        // Handle cases where the value might contain commas or quotes
        const value = item[header];
        const stringValue =
          value === null || value === undefined
            ? ""
            : typeof value === "object"
              ? JSON.stringify(value).replace(/"/g, '""')
              : String(value);

        // Wrap in quotes if the value contains commas, quotes, or newlines
        return /[,"\n\r]/.test(stringValue)
          ? `"${stringValue.replace(/"/g, '""')}"`
          : stringValue;
      })
      .join(",");

    csvContent += row + "\n";
  });

  return csvContent;
};

// Utility function to trigger CSV download
export const downloadCSV = (csvContent: string, filename: string) => {
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.setAttribute("href", url);
  link.setAttribute("download", filename);
  link.style.visibility = "hidden";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Helper function to get day of week from index (0-6)
export const getDayOfWeek = (index: number): string => {
  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];
  return index >= 0 && index < days.length ? days[index] : "Unknown";
};
